import { DropdownButtonType, List, ListItem, SingleSelectDropdown, TabStyles, TextLinkWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';
import { style } from 'typestyle';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { Tableau } from '~src/commonInfra/utils/TableauLog';

import {
  CompositeSessionInfoSelectors,
  SessionServerSelectors
} from '../../../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { ILanguage } from '../../../../../httpClient/serverApi/types/ILanguage';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { dropdownListStyle, learnMoreStyle } from '../../../../styles/CommonStyles';
import { LanguagesInstance } from '../../../resources/unownedSettings/Languages';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

export const DEFAULT_SITE_LANGUAGE_FIELD = 'settings.siteSettingsV2.defaultSiteLanguage';
export const DEFAULT_SITE_LANGUAGE_SECTION_TEST_ID = 'default-site-language-section';
export const DEFAULT_SITE_LANGUAGE_DROPDOWN_TEST_ID = 'default-site-language-dropdown';
export const DEFAULT_SITE_LANGUAGE_LEARN_MORE_TEST_ID = 'default-site-language-learn-more';

const UNSPECIFIED_VALUE = 'UnspecifiedLanguage';

const dropdownLabelStyle = style({
  marginBottom: TabStyles.Spacing.SmallSpace
});

export type DefaultSiteLanguageSectionProps = SiteSettingsForSiteAdminProps<'siteSettingsV2'>;

export function DefaultSiteLanguageSection(): React.ReactElement | null {
  const formik = useFormikContext<DefaultSiteLanguageSectionProps>();
  const showSection = useShowSettingsSection();
  const items = useLanguageListItems(showSection);

  if (!showSection) {
    return null;
  }

  const stored = formik.values.settings?.siteSettingsV2?.defaultSiteLanguage;
  const displayValue = stored == null ? UNSPECIFIED_VALUE : stored;

  const handleChange = (value: string) => {
    formik.setFieldValue(
      DEFAULT_SITE_LANGUAGE_FIELD,
      value === UNSPECIFIED_VALUE ? null : value
    );
  };

  return (
    <SettingsSection
      title={Messages.languageForEmailsTitle()}
      description={
        <>
          {Messages.languageForEmailsDescription()}
          &nbsp;
          <TextLinkWidget
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getHelpUrlByCategory('email-notification-language')}
            testId={DEFAULT_SITE_LANGUAGE_LEARN_MORE_TEST_ID}>
            {Messages.learnMore()}
          </TextLinkWidget>
        </>
      }
      testId={DEFAULT_SITE_LANGUAGE_SECTION_TEST_ID}>
      <div className={dropdownLabelStyle}>{Messages.language()}</div>
      <SingleSelectDropdown
        list={<List items={items} className={dropdownListStyle} />}
        type={DropdownButtonType.Outline}
        value={displayValue}
        onChange={handleChange}
        label={''}
        aria-label={Messages.language()}
        testId={DEFAULT_SITE_LANGUAGE_DROPDOWN_TEST_ID}
      />
    </SettingsSection>
  );
}

function useShowSettingsSection(): boolean {
  const isOnlineEnvironment = !!useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isSiteOrServerAdmin = !!useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  return FeatureFlags.uiamEmailLocalizationEnabled && isOnlineEnvironment && isSiteOrServerAdmin;
}

function useLanguageListItems(enabled: boolean): ListItem[] {
  const [items, setItems] = React.useState<ListItem[]>([]);

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    let isMounted = true;
    LanguagesInstance.getAll()
      .then((languages: ILanguage[]) => {
        if (!isMounted) {
          return;
        }
        const withUnspecified = [LanguagesInstance.unspecifiedLanguage()].concat(languages);
        const mapped: ListItem[] = withUnspecified.map((lang: ILanguage) => ({
          content: lang.displayName,
          value: lang.language ? lang.language : UNSPECIFIED_VALUE,
          testId: lang.language ? lang.language : UNSPECIFIED_VALUE
        }));
        setItems(mapped);
      })
      .catch((error: Error) => {
        Tableau.Logger().error(error);
      });
    return () => {
      isMounted = false;
    };
  }, [enabled]);

  return items;
}

export const DefaultSiteLanguageSectionSpecs = componentRegistryEntry({
  name: 'DefaultSiteLanguageSection',
  component: DefaultSiteLanguageSection
});
